// src/components/common/BottomSheet.tsx
import React, { useEffect, useRef } from 'react';
import styles from '@/assets/app/css/bottomSheet.module.css';

interface BottomSheetStyle {
  backgroundColor?: string;
}
interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  showHeader?: boolean;
  styleOptions?: BottomSheetStyle;
  themeColor?: string;
  /** 'bottom'(기본값): 하단에서 올라오는 시트, 'center': 화면 중앙 다이얼로그 */
  position?: 'bottom' | 'center';
}

/**
 * 하단 팝업 시트 (공통 컴포넌트)
 * - 시대선택 등 하단에서 올라오는 팝업
 * - Tailwind CSS + tw-animate-css 사용
 */
const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  title,
  children,
  showHeader = false,
  styleOptions,
  themeColor,
  position = 'bottom',
}) => {
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {/* 오버레이 */}
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-black/50 z-[998] transition-opacity duration-300 ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {/* 시트 컨테이너 */}
      <div
        ref={sheetRef}
        className={
          position === 'center'
            ? `${themeColor ? `theme-${themeColor}` : 'bg-white'} fixed left-1/2 top-1/2 w-[85%] max-w-[340px] rounded-[20px] overflow-hidden h-[46vh] z-[999] shadow-[0_4px_20px_rgba(0,0,0,0.2)] transition-all duration-300 ease-out flex flex-col ${
                isOpen
                  ? '-translate-x-1/2 -translate-y-1/2 scale-100 opacity-100'
                  : '-translate-x-1/2 -translate-y-1/2 scale-95 opacity-0 pointer-events-none'
              }`
            : `${themeColor ? `theme-${themeColor}` : 'bg-white'} fixed left-0 right-0 bottom-0 rounded-t-[20px] overflow-hidden max-h-[75vh] z-[999] shadow-[0_-4px_16px_rgba(0,0,0,0.15)] transition-transform duration-300 ease-out flex flex-col ${
                isOpen ? 'translate-y-0' : 'translate-y-full'
              }`
        }
      >
        {/* 헤더 */}
        {showHeader && (
          <div className="flex justify-center items-center px-6 pt-3 pb-3 border-b border-gray-200">
            {title && <h3 className="text-lg font-bold text-gray-800 m-0">{title}</h3>}
            <button
              onClick={onClose}
              aria-label="닫기"
              className={`x-button ${styles.closeButton}`}
            ></button>
          </div>
        )}

        {/* 콘텐츠 */}
        <div
          className={
            position === 'center'
              ? `px-4 py-5 overflow-y-scroll flex-1 min-h-0 flex flex-col ${styles.centerScroll}`
              : `px-4 py-5 overflow-y-auto flex-1 min-h-0 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100 scrollbar-thumb-rounded-full scrollbar-track-rounded-full`
          }
          style={{
            backgroundColor: themeColor ? 'var(--color-book-bg)' : 'white',
          }}
        >
          {children}
        </div>
      </div>
    </>
  );
};

export default BottomSheet;
